import type { DropdownOption } from '@sparklayer-io/spark-dash-ui/components/ui/dropdown/types';
import { Alert, Button, Dropdown, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import localeList from '../../../utils/locale-iso639-1';
import { useThemeEditor } from '../context/theme-editor-context';
import type { FileType } from '../types/theme';

function languageOptions(): DropdownOption[] {
  const names = new Intl.DisplayNames(['en'], { type: 'language' });
  return localeList.map((locale) => {
    const canonical = Intl.getCanonicalLocales(locale)[0] ?? locale;
    return { value: canonical, label: names.of(canonical) ?? canonical };
  });
}

export function AddFileModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { coreFiles, theme, createNewFile } = useThemeEditor();
  const [selectedType, setSelectedType] = useState<FileType | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const fileTypes = useMemo(
    () => ['language', ...Object.keys(coreFiles)] as FileType[],
    [coreFiles],
  );

  const options = useMemo<DropdownOption[]>(() => {
    if (!selectedType) {
      return [];
    }
    if (selectedType === 'language') {
      return languageOptions();
    }
    const created = new Set(
      (theme?.files ?? []).filter((file) => file.type === selectedType).map((file) => file.slug),
    );
    return (coreFiles[selectedType] ?? [])
      .filter((slug) => !created.has(slug))
      .map((slug) => ({ value: slug, label: slug }));
  }, [selectedType, coreFiles, theme]);

  function reset() {
    setSelectedType(null);
    setSelectedSlug(null);
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function selectType(type: FileType) {
    setSelectedSlug(null);
    setSelectedType((current) => (current === type ? null : type));
  }

  async function handleAdd() {
    if (!selectedType || !selectedSlug) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await createNewFile(selectedType, selectedSlug);
      handleClose();
    } catch {
      setError(t('features:themes.pages.theme_editor_page.add_file.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: t('features:themes.pages.theme_editor_page.add_file.submit'),
        onClick: () => void handleAdd(),
        disabled: !selectedSlug || saving,
        loading: saving,
      }}
      secondaryAction={{ text: t('buttons:cancel'), onClick: handleClose }}
    >
      <Modal.Header>{t('features:themes.pages.theme_editor_page.add_file.title')}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <Text variant="secondary">
            {t('features:themes.pages.theme_editor_page.add_file.subheading')}
          </Text>

          {error && (
            <Alert headingLevel="h3" variant="error">
              {error}
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <Text bold>{t('features:themes.pages.theme_editor_page.add_file.file_types')}</Text>
            <div className="flex flex-wrap gap-2">
              {fileTypes.map((type) => (
                <Button
                  key={type}
                  size="sm"
                  variant={type === selectedType ? 'primary' : 'secondary'}
                  onClick={() => selectType(type)}
                >
                  {t(`features:themes.pages.theme_editor_page.file_types.${type}`)}
                </Button>
              ))}
            </div>
          </div>

          {selectedType && (
            <Dropdown
              options={options}
              value={selectedSlug ?? ''}
              onChange={setSelectedSlug}
              placeholder={
                options.length
                  ? t('features:themes.pages.theme_editor_page.add_file.select_placeholder')
                  : t('features:themes.pages.theme_editor_page.add_file.none_available')
              }
              disabled={!options.length}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
